/**
 * FormMultipage
 * Single-page multi-step form with breadcrumb navigation,
 * per-step validation, toggle panels, and summary rendering.
 */


class FormMultipage {

  static init = (params) => {
    const allForms = document.querySelectorAll(`${params.selForm}:not(.is-initialized)`);

    allForms.forEach(form => {
      new FormMultipage(form, params);
    })
  }



  constructor(form, params) {
    this.form = form;
    this.selSection = params.selSection;
    this.selBreadcrumbItem = params.selBreadcrumbItem;
    this.selBreadcrumbLink = params.selBreadcrumbLink;
    this.selNextBtn = params.selNextBtn;
    this.selPrevBtn = params.selPrevBtn;
    this.selSummary = params.selSummary;
    this.selFestivalToggle = params.selFestivalToggle;
    this.selCarPassesToggle = params.selCarPassesToggle;
    this.selWorkshopToggle = params.selWorkshopToggle;
    this.selPhoneField = params.selPhoneField;
    this.selFestivalParticipantsField = 'input[name*="[participants]"]';
    this.phonePattern = /^\+?(\([\d\/\-\s#\*\.]+\)|[\d\/\-\s#\*\.]+)+$/;

    this.sections = Array.from(this.form.querySelectorAll(this.selSection));
    this.breadcrumbItems = Array.from(
      this.form.closest('.m-ngoForm').querySelectorAll(this.selBreadcrumbItem)
    );
    this.currentStep = 1;
    this.visitedSteps = new Set([1]);
    this.totalSteps = this.sections.length;

    this.markInitialized();
    this.initNavigation();
    this.initBreadcrumb();
    this.initFestivalToggles();
    this.initCarPassesToggles();
    this.initFestivalCarPassCalculation();
    this.initAdditionalCarPassesSummary();
    this.initWorkshopToggle();
    this.initPhoneValidation();
    this.initFestivalSelectionValidation();
    this.initFestivalParticipantsValidation();
    this.updateBreadcrumbState();
  }



  markInitialized = () => {
    this.form.classList.add('is-initialized');
  }



  // ── Navigation ──────────────────────────────────────

  initNavigation = () => {
    this.form.addEventListener('click', (e) => {
      const nextBtn = e.target.closest(this.selNextBtn);
      const prevBtn = e.target.closest(this.selPrevBtn);

      if (nextBtn) {
        e.preventDefault();
        const target = parseInt(nextBtn.dataset.next, 10);
        this.goToStep(target, true);
      }

      if (prevBtn) {
        e.preventDefault();
        const target = parseInt(prevBtn.dataset.prev, 10);
        this.goToStep(target, false);
      }
    });
  }



  goToStep = (targetStep, validateCurrent) => {
    if (targetStep < 1 || targetStep > this.totalSteps) return;

    if (validateCurrent && !this.validateCurrentSection()) return;

    this.visitedSteps.add(this.currentStep);
    this.currentStep = targetStep;
    this.visitedSteps.add(targetStep);

    this.showSection(targetStep);
    this.updateBreadcrumbState();
    this.scrollToForm();

    if (this.isSummaryStep(targetStep)) {
      this.renderSummary();
    }
  }



  showSection = (sectionNumber) => {
    this.sections.forEach(section => {
      const num = parseInt(section.dataset.section, 10);

      if (num === sectionNumber) {
        section.classList.remove('is-hidden');
      } else {
        section.classList.add('is-hidden');
      }
    });
  }



  scrollToForm = () => {
    const wrapper = this.form.closest('.m-ngoForm');
    if (wrapper) {
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }



  isSummaryStep = (step) => {
    return step === this.totalSteps;
  }



  // ── Breadcrumb ──────────────────────────────────────

  initBreadcrumb = () => {
    const container = this.form.closest('.m-ngoForm');
    if (!container) return;

    container.addEventListener('click', (e) => {
      const link = e.target.closest(this.selBreadcrumbLink);
      if (!link) return;

      const target = parseInt(link.dataset.target, 10);
      if (this.visitedSteps.has(target) || target === this.currentStep) {
        this.goToStep(target, target > this.currentStep);
      }
    });
  }



  updateBreadcrumbState = () => {
    this.breadcrumbItems.forEach(item => {
      const num = parseInt(item.dataset.breadcrumb, 10);
      const link = item.querySelector(this.selBreadcrumbLink);
      const target = link ? parseInt(link.dataset.target, 10) : num;

      item.classList.remove('is-active', 'is-visited', 'is-disabled');

      if (target === this.currentStep) {
        item.classList.add('is-active');
      } else if (this.visitedSteps.has(target)) {
        item.classList.add('is-visited');
      } else {
        item.classList.add('is-disabled');
      }
    });
  }



  // ── Validation ──────────────────────────────────────

  validateCurrentSection = () => {
    const currentSection = this.sections.find(
      s => parseInt(s.dataset.section, 10) === this.currentStep
    );

    if (!currentSection) return true;

    const fields = currentSection.querySelectorAll('input, textarea, select');
    let firstInvalid = null;

    for (const field of fields) {
      if (this.isFieldHidden(field)) continue;
      this.applyCustomValidation(field);
      if (!field.checkValidity()) {
        if (!firstInvalid) firstInvalid = field;
        const formItem = field.closest('.c-form__item');
        if (formItem) {
          formItem.classList.add('has-error');
        }
      } else {
        // remove "has-error" class if field is valid on re-check
        const formItem = field.closest('.c-form__item');
        if (formItem) {
          formItem.classList.remove('has-error');
        }
      }
    }

    if (firstInvalid) {
      firstInvalid.reportValidity();
      return false;
    }

    return true;
  }



  initPhoneValidation = () => {
    if (!this.selPhoneField) return;

    this.form.addEventListener('input', (e) => {
      if (!e.target.matches(this.selPhoneField)) return;
      this.applyCustomValidation(e.target);
    });

    const phoneFields = this.form.querySelectorAll(this.selPhoneField);
    phoneFields.forEach((field) => {
      this.applyCustomValidation(field);
    });
  }


  initFestivalSelectionValidation = () => {
    const festivalToggles = this.form.querySelectorAll(this.selFestivalToggle);
    if (!festivalToggles.length) return;

    this.form.addEventListener('change', (e) => {
      if (!e.target.matches(this.selFestivalToggle)) return;
      this.applyFestivalSelectionValidation(e.target.closest(this.selSection));
    });

    this.applyFestivalSelectionValidation(festivalToggles[0].closest(this.selSection));
  }


  applyFestivalSelectionValidation = (section) => {
    if (!section) return;

    const festivalToggles = Array.from(section.querySelectorAll(this.selFestivalToggle));
    if (!festivalToggles.length) return;

    const hasSelectedFestival = festivalToggles.some((toggle) => toggle.checked);

    festivalToggles.forEach((toggle, index) => {
      const formItem = toggle.closest('.c-form__item');
      const isPrimaryValidationTarget = index === 0;

      toggle.setCustomValidity(
        !hasSelectedFestival && isPrimaryValidationTarget
          ? 'Bitte wähle mindestens ein Festival aus.'
          : ''
      );

      if (formItem) {
        if (hasSelectedFestival) {
          formItem.classList.remove('has-error');
        }
      }
    });
  }


  initFestivalParticipantsValidation = () => {
    this.form.addEventListener('input', (e) => {
      if (!e.target.matches(this.selFestivalParticipantsField)) return;
      this.applyCustomValidation(e.target);
    });

    this.form.addEventListener('change', (e) => {
      if (e.target.matches(this.selFestivalToggle)) {
        const participantsInput = this.getFestivalParticipantsField(e.target.dataset.festival);
        if (participantsInput) {
          this.applyCustomValidation(participantsInput);
        }
        return;
      }

      if (!e.target.matches(this.selFestivalParticipantsField)) return;
      this.applyCustomValidation(e.target);
    });

    const participantFields = this.form.querySelectorAll(this.selFestivalParticipantsField);
    participantFields.forEach((field) => {
      this.applyCustomValidation(field);
    });
  }


  getFestivalParticipantsField = (festivalId) => {
    return this.form.querySelector(`input[name*="[${festivalId}][participants]"]`);
  }


  isFestivalParticipantsRequired = (field) => {
    const panel = field.closest('[data-festival-panel]');
    if (!panel) return false;

    const festivalId = panel.dataset.festivalPanel;
    if (!festivalId) return false;

    const toggle = this.form.querySelector(
      `${this.selFestivalToggle}[data-festival="${festivalId}"]`
    );

    return !!(toggle && toggle.checked);
  }



  applyCustomValidation = (field) => {
    if (!field || typeof field.setCustomValidity !== 'function') return;

    field.setCustomValidity('');

    if (this.selPhoneField && field.matches(this.selPhoneField)) {
      const value = field.value.trim();
      const isValidPhone = value === '' || this.phonePattern.test(value);
      field.setCustomValidity(isValidPhone ? '' : 'Please enter a valid telephone number.');
      return;
    }

    if (field.matches(this.selFestivalToggle)) {
      this.applyFestivalSelectionValidation(field.closest(this.selSection));
      return;
    }

    if (field.matches(this.selFestivalParticipantsField)) {
      const value = field.value.trim();

      if (!this.isFestivalParticipantsRequired(field)) {
        return;
      }

      if (value === '') {
        field.setCustomValidity('Please enter the number of participants for this festival.');
        return;
      }

      const isWholeNumber = /^\d+$/.test(value);
      field.setCustomValidity(
        isWholeNumber ? '' : 'Please enter a whole number of participants.'
      );
    }
  }



  isFieldHidden = (field) => {
    // Check if field input type itself is hidden
    if (field.type === 'hidden') return true;
    // Check if any parent has "is-hidden" class
    const hiddenParent = field.closest('.is-hidden');
    return hiddenParent !== null;
  }



  // ── Festival Toggle ─────────────────────────────────

  initFestivalToggles = () => {
    const festivalToggles = this.form.querySelectorAll(this.selFestivalToggle);
    festivalToggles.forEach((toggle) => {
      const festivalId = toggle.dataset.festival;
      const panel = this.form.querySelector(`[data-festival-panel="${festivalId}"]`);
      if (!panel) return;

      if (toggle.checked) {
        panel.classList.remove('is-hidden');
      } else {
        panel.classList.add('is-hidden');
      }
    });

    this.form.addEventListener('change', (e) => {
      if (!e.target.matches(this.selFestivalToggle)) return;

      const festivalId = e.target.dataset.festival;
      const panel = this.form.querySelector(`[data-festival-panel="${festivalId}"]`);
      if (!panel) return;

      if (e.target.checked) {
        panel.classList.remove('is-hidden');
        const participantsInput = panel.querySelector(this.selFestivalParticipantsField);
        if (participantsInput) {
          this.syncFestivalCarPassesFromParticipants(participantsInput);
        }
      } else {
        panel.classList.add('is-hidden');
        this.clearPanelFields(panel);
        this.resetFestivalCarPasses(panel);
      }
    });
  }



  // ── Festival Car Pass Calculation ──────────────────

  initFestivalCarPassCalculation = () => {
    const participantSelector = this.selFestivalParticipantsField;

    this.form.addEventListener('input', (e) => {
      if (!e.target.matches(participantSelector)) return;
      this.syncFestivalCarPassesFromParticipants(e.target);
    });

    this.form.addEventListener('change', (e) => {
      if (!e.target.matches(participantSelector)) return;
      this.syncFestivalCarPassesFromParticipants(e.target);
    });

    // Initialize hidden values and labels for prefilled/restored forms.
    const participantInputs = this.form.querySelectorAll(participantSelector);
    participantInputs.forEach((participantsInput) => {
      this.syncFestivalCarPassesFromParticipants(participantsInput);
    });
  }



  syncFestivalCarPassesFromParticipants = (participantsInput) => {
    const panel = participantsInput.closest('[data-festival-panel]');
    if (!panel) return;

    const carPassesInput = panel.querySelector('input[name*="[carPasses]"]');
    if (!carPassesInput) return;

    const calculatedSpan = panel.querySelector('.calculated');
    const participants = Math.max(0, parseInt(participantsInput.value, 10) || 0);
    const min = parseInt(carPassesInput.min, 10);
    const max = parseInt(carPassesInput.max, 10);

    const rawCarPasses = participants > 0 ? Math.ceil(participants / 4) : 0;
    // Clamp calculated car passes to field constraints before syncing UI/value.
    const clampedCarPasses = Math.min(
      Number.isNaN(max) ? Infinity : max,
      Math.max(Number.isNaN(min) ? 0 : min, rawCarPasses)
    );

    carPassesInput.value = String(clampedCarPasses);
    if (calculatedSpan) {
      calculatedSpan.textContent = String(clampedCarPasses);
    }

    this.updateFestivalCarPassesSummary(panel);
  }



  resetFestivalCarPasses = (panel) => {
    const carPassesInput = panel.querySelector('input[name*="[carPasses]"]');
    const calculatedSpan = panel.querySelector('.calculated');

    if (carPassesInput) {
      carPassesInput.value = '0';
    }

    if (calculatedSpan) {
      calculatedSpan.textContent = '0';
    }

    this.updateFestivalCarPassesSummary(panel);
  }



  // ── Additional Car Passes Toggle ────────────────────

  initAdditionalCarPassesSummary = () => {
    const additionalSelector = 'input[name*="[additionalCarPasses]"]';

    this.form.addEventListener('input', (e) => {
      if (!e.target.matches(additionalSelector)) return;
      const festivalPanel = e.target.closest('[data-festival-panel]');
      if (!festivalPanel) return;
      this.updateFestivalCarPassesSummary(festivalPanel);
    });

    this.form.addEventListener('change', (e) => {
      if (!e.target.matches(additionalSelector)) return;
      const festivalPanel = e.target.closest('[data-festival-panel]');
      if (!festivalPanel) return;
      this.updateFestivalCarPassesSummary(festivalPanel);
    });

    const festivalPanels = this.form.querySelectorAll('[data-festival-panel]');
    festivalPanels.forEach((festivalPanel) => {
      this.updateFestivalCarPassesSummary(festivalPanel);
    });
  }



  initCarPassesToggles = () => {
    const carPassesToggles = this.form.querySelectorAll(this.selCarPassesToggle);
    carPassesToggles.forEach((toggle) => {
      const festivalId = toggle.dataset.festival;
      const panel = this.form.querySelector(`[data-additional-carpasses-panel="${festivalId}"]`);
      if (!panel) return;

      if (toggle.checked) {
        panel.classList.remove('is-hidden');
      } else {
        panel.classList.add('is-hidden');
      }
    });

    this.form.addEventListener('change', (e) => {
      if (!e.target.matches(this.selCarPassesToggle)) return;
      const festivalId = e.target.dataset.festival;
      const panel = this.form.querySelector(`[data-additional-carpasses-panel="${festivalId}"]`);
      const festivalPanel = this.form.querySelector(`[data-festival-panel="${festivalId}"]`);
      if (!panel) return;

      if (e.target.checked) {
        panel.classList.remove('is-hidden');
      } else {
        panel.classList.add('is-hidden');
        this.clearPanelFields(panel);
      }

      if (festivalPanel) {
        this.updateFestivalCarPassesSummary(festivalPanel);
      }
    });
  }



  updateFestivalCarPassesSummary = (festivalPanel) => {
    const standardInput = festivalPanel.querySelector('input[name*="[carPasses]"]');
    const additionalInput = festivalPanel.querySelector('input[name*="[additionalCarPasses]"]');
    const standardSpan = festivalPanel.querySelector('.calculatedStandard');
    const extraSpan = festivalPanel.querySelector('.requestedExtra');
    const totalSpan = festivalPanel.querySelector('.totalCarPasses');

    const standard = Math.max(0, parseInt(standardInput && standardInput.value, 10) || 0);
    const extraRaw = Math.max(0, parseInt(additionalInput && additionalInput.value, 10) || 0);
    const extraMin = additionalInput ? parseInt(additionalInput.min, 10) : NaN;
    const extraMax = additionalInput ? parseInt(additionalInput.max, 10) : NaN;
    const hasExtra = extraRaw > 0;

    const extra = Math.min(
      Number.isNaN(extraMax) ? Infinity : extraMax,
      Math.max(Number.isNaN(extraMin) ? 0 : extraMin, extraRaw)
    );

    if (standardSpan) {
      standardSpan.textContent = String(standard);
    }
    if (extraSpan) {
      extraSpan.textContent = String(hasExtra ? extra : 0);
    }
    if (totalSpan) {
      totalSpan.textContent = String(standard + (hasExtra ? extra : 0));
    }
  }



  // ── Workshop Toggle ─────────────────────────────────

  initWorkshopToggle = () => {
    const panel = this.form.querySelector('[data-workshop-panel="yes"]');
    const workshopToggle = this.form.querySelector(this.selWorkshopToggle);
    if (!panel || !workshopToggle) return;

    this.toggleWorkshopPanel(panel, workshopToggle.checked);

    this.form.addEventListener('change', (e) => {
      if (!e.target.matches(this.selWorkshopToggle)) return;
      this.toggleWorkshopPanel(panel, e.target.checked);
    });
  }



  toggleWorkshopPanel = (panel, isVisible) => {
    if (isVisible) {
      panel.classList.remove('is-hidden');
      return;
    }

    panel.classList.add('is-hidden');
    this.clearPanelFields(panel);
  }



  // ── Utility: Clear Fields ───────────────────────────

  clearPanelFields = (panel) => {
    const fields = panel.querySelectorAll('input, textarea, select');

    fields.forEach(field => {
      if (field.type === 'radio' || field.type === 'checkbox') {
        field.checked = false;
      } else if (field.type !== 'hidden') {
        field.value = '';
      }
    });
  }



  // ── Summary Rendering ──────────────────────────────

  renderSummary = () => {
    const summary = this.form.querySelector(this.selSummary);
    if (!summary) return;

    this.renderSimpleSummaryFields(summary);
    this.renderFestivalSummary(summary);
    this.renderFileSummary(summary);
    this.renderWorkshopSummary(summary);
  }



  renderSimpleSummaryFields = (summary) => {
    const simpleFields = [
      'organizationName',
      'projectDescription',
      'festivalExperience',
      'website',
      'planningContactFirstName',
      'planningContactLastName',
      'planningContactEmail',
      'planningContactPhone',
      'boothActivities',
      'conceptImplemented',
      'boothImpact',
      'boothSize',
      'extraSpaceDescription',
      'boothDescription',
      'materials',
      'additionalNotes'
    ];

    simpleFields.forEach(fieldName => {
      const dd = summary.querySelector(`[data-summary="${fieldName}"]`);
      if (!dd) return;

      const input = this.form.querySelector(`[name*="[${fieldName}]"]`);
      dd.textContent = input && input.value ? input.value : '–';
    });
  }



  renderFestivalSummary = (summary) => {
    const container = summary.querySelector('[data-summary="festivals"]');
    if (!container) return;

    container.innerHTML = '';

    const festivalItems = this.form.querySelectorAll('[data-festival]');
    const processedFestivals = new Set();

    festivalItems.forEach(item => {
      if (!item.classList.contains('c-form__festivalItem')) return;

      const festivalId = item.dataset.festival;
      if (processedFestivals.has(festivalId)) return;
      processedFestivals.add(festivalId);

      const nameInput = this.form.querySelector(
        `input[name*="[${festivalId}][festivalName]"]`
      );
      const festivalName = nameInput ? nameInput.value : festivalId;

      const participateCheckbox = this.form.querySelector(
        `input[type="checkbox"][name*="[${festivalId}][participate]"]`
      );
      const participates = participateCheckbox && participateCheckbox.checked;

      const dl = document.createElement('dl');
      dl.className = 'c-form__summaryList';

      if (participates) {
        const participantsInput = this.form.querySelector(
          `input[name*="[${festivalId}][participants]"]`
        );
        const carPassesInput = this.form.querySelector(
          `input[name*="[${festivalId}][carPasses]"]`
        );
        const additionalCarPassesInput = this.form.querySelector(
          `input[name*="[${festivalId}][additionalCarPasses]"]`
        );

        dl.innerHTML =
          `<dt>${festivalName}:</dt><dd>Teilnahme</dd>` +
          (participantsInput && participantsInput.value
            ? `<dt>Teilnehmende:</dt><dd>${participantsInput.value}</dd>`
            : '') +
          (carPassesInput && carPassesInput.value
            ? `<dt>Carpässe:</dt><dd>${carPassesInput.value}</dd>`
            : '') +
          (additionalCarPassesInput && additionalCarPassesInput.value
            ? `<dt>zusätzliche Carpässe:</dt><dd>${additionalCarPassesInput.value}</dd>`
            : '');
      } else {
        dl.innerHTML = `<dt>${festivalName}:</dt><dd>Keine Teilnahme</dd>`;
      }

      container.appendChild(dl);
    });
  }



  renderFileSummary = (summary) => {
    const dd = summary.querySelector('[data-summary="boothImage"]');
    if (!dd) return;

    const fileInput = this.form.querySelector('input[type="file"]');

    if (fileInput && fileInput.files && fileInput.files.length > 0) {
      dd.textContent = fileInput.files[0].name;
      return;
    }

    const fileNameHidden = this.form.querySelector('[name*="[boothImageFileName]"]');
    if (fileNameHidden && fileNameHidden.value) {
      dd.textContent = fileNameHidden.value;
      return;
    }

    dd.textContent = 'Kein Bild hochgeladen';
  }



  renderWorkshopSummary = (summary) => {
    const dd = summary.querySelector('[data-summary="workshopInterest"]');
    if (!dd) return;

    const workshopRadio = this.form.querySelector(
      'input[name*="[workshopInterest]"]:checked'
    );

    const workshopSection = summary.querySelector('[data-summary-workshop]');
    const interested = workshopRadio && workshopRadio.value === '1';

    dd.textContent = interested ? 'Ja' : 'Nein';

    if (workshopSection) {
      if (interested) {
        workshopSection.style.display = '';
      } else {
        workshopSection.style.display = '';
      }
    }
  }
}
